import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, X, MapPinLine } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SiteVisits() {
  const [items, setItems] = useState([]);
  const [leads, setLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ lead_id: "", property_id: "", scheduled_at: "", feedback: "", status: "Scheduled" });

  const load = async () => {
    const [sv, ld, pr] = await Promise.all([api.get("/sitevisits"), api.get("/leads"), api.get("/properties")]);
    setItems(sv.data); setLeads(ld.data); setProperties(pr.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.lead_id || !form.scheduled_at) return;
    try {
      await api.post("/sitevisits", { ...form, scheduled_at: new Date(form.scheduled_at).toISOString() });
      toast.success("Site visit scheduled + follow-up auto-created");
      setShow(false);
      setForm({ lead_id: "", property_id: "", scheduled_at: "", feedback: "", status: "Scheduled" });
      await load();
    } catch { toast.error("Failed"); }
  };

  const badge = (s) => ({
    Scheduled: "text-sky-300 bg-sky-400/10 border-sky-400/20",
    Completed: "text-emerald-300 bg-emerald-400/10 border-emerald-400/20",
    Cancelled: "text-rose-300 bg-rose-400/10 border-rose-400/20",
  }[s] || "");

  return (
    <div className="space-y-6" data-testid="sitevisits-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Field Ops</div>
          <h1 className="font-serif text-3xl md:text-4xl text-white mt-1">Site Visits</h1>
        </div>
        <button onClick={() => setShow(true)} data-testid="new-sitevisit-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#D4AF37] text-[#060B14] font-semibold text-sm hover:bg-[#F2CE6E]">
          <Plus weight="bold" /> Schedule Visit
        </button>
      </div>

      <div className="card-luxury overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#060B14] border-b border-[#1E293B]">
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-400">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Lead</th>
                <th className="px-4 py-3">Property</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Feedback</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-500">No visits scheduled.</td></tr>}
              {items.map(v => {
                const prop = properties.find(p => p.id === v.property_id);
                return (
                  <tr key={v.id} className="border-b border-[#1E293B]" data-testid="sitevisit-row">
                    <td className="px-4 py-3 text-white">{new Date(v.scheduled_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <div className="text-white">{v.lead?.name || "—"}</div>
                      <div className="text-xs text-slate-400">{v.lead?.mobile}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{prop?.project_name || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${badge(v.status)}`}>{v.status}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-300 text-xs">{v.feedback || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShow(false)}>
          <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-[#0A1120] border border-[#1E293B] rounded-xl w-full max-w-xl" data-testid="sv-form">
            <div className="p-5 border-b border-[#1E293B] flex items-center justify-between">
              <h3 className="font-serif text-xl text-white flex items-center gap-2"><MapPinLine size={20} weight="duotone" className="text-[#D4AF37]" /> Schedule Site Visit</h3>
              <button type="button" onClick={() => setShow(false)} className="text-slate-400"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Lead*</label>
                <select required value={form.lead_id} onChange={e => setForm({ ...form, lead_id: e.target.value })} data-testid="sv-lead"
                  className="mt-1 w-full bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm">
                  <option value="">— Select lead —</option>
                  {leads.map(l => <option key={l.id} value={l.id}>{l.name} • {l.mobile}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Property</label>
                <select value={form.property_id} onChange={e => setForm({ ...form, property_id: e.target.value })} data-testid="sv-property"
                  className="mt-1 w-full bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm">
                  <option value="">— Select property —</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.project_name} • {p.sector}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Scheduled At*</label>
                <input type="datetime-local" required value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} data-testid="sv-time"
                  className="mt-1 w-full bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                  className="mt-1 w-full bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm">
                  {["Scheduled", "Completed", "Cancelled"].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Feedback</label>
                <textarea rows={3} value={form.feedback} onChange={e => setForm({ ...form, feedback: e.target.value })}
                  className="mt-1 w-full bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm" />
              </div>
            </div>
            <div className="p-5 border-t border-[#1E293B] flex justify-end gap-2">
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm text-slate-300">Cancel</button>
              <button data-testid="sv-save-btn" className="px-4 py-2 rounded-md bg-[#D4AF37] text-[#060B14] font-semibold text-sm hover:bg-[#F2CE6E]">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
